
import { debounce } from "lodash";
import { Getter } from "vuex-class";
import api from "@/services/api.service";
import axios, { CancelTokenSource } from "axios";
import { ServerDetails } from "@/store/devices/types";
import { Component, Mixins } from "vue-property-decorator";
import { AreaNode } from "@/types/sv-data/groups/AreaNode";
import { getDebouncePeriod } from "@/utils/debounce-helper";
import { ShareWithPerson } from "@/store/site-monitor/types";
import { ModalItem } from "../table/generic-update-modal.vue";
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import ContactGroup from "@/types/sv-data/contact-groups/ContactGroup";
import GenericTable, { TableHeader } from "../table/generic-table.vue";
import { PaginatedSearchQueryParams, UserPermissions } from "@/store/types";
import IntegrationTemplateApi from "@/services/api.integrationTemplate.service";
import PaginatedSearch, { PaginatedSearchRequest } from "@/mixins/PaginatedSearch";
import { IntegrationTemplate } from "@/store/tasks/notification/IntegrationTemplate.ts";
import { IntegrationTemplateContact } from "@/store/tasks/notification/IntegrationTemplateContact";
import { IntegrationTemplateContactType } from "@/store/tasks/notification/IntegrationTemplateContactType";
import IntegrationTemplatePagedRequest from "@/types/sv-data/integration-templates/IntegrationTemplatePagedRequest";

@Component({
	components: {
		"generic-table": GenericTable,
	},
})
export default class IntegrationTemplateSetup extends Mixins(PaginatedSearch) {
	@Getter("getPermissions") private permissions!: UserPermissions;
	@Getter("getFeature") getFeature!: (featureName: string[]) => boolean;

	private contactGroups: ContactGroup[] = [];
	private contactGroupSearchToken: CancelTokenSource | null = null;
	private integrationTemplates: IntegrationTemplate[] = [];
	private isLoading: boolean = false;
	private readonly debouncePeriod: number = process.env.NODE_ENV === "test" ? 1 : 500;
	private searchedContacts: IntegrationTemplateContact[] = [];
	private selectedItem: IntegrationTemplateContact | null = null;
	private selectedUsers: IntegrationTemplateContact[] = [];
	private servers: ServerDetails[] = [];
	private showSyncWarning: boolean = false;
	private totalRecords: number = 0;

	/**
	 * Retrieves the table columns for the integration templates.
	 * @returns {TableHeader[]}
	 */
	private get columns(): TableHeader[] {
		const _columns: TableHeader[] = [
			{
				title: "Title",
				key: "title",
				order: 1,
				dataType: "input",
				description: "Integration Template Title",
				sortable: true,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				isTermLabel: true,
				isSortedByDefault: true,
			},
			{
				title: "Area",
				key: "areaTitle",
				order: 2,
				dataType: "input",
				description: "Area Title",
				sortable: false,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
			},
			{
				title: "Recipients",
				key: "recipientCount",
				order: 3,
				description: "Number of Recipients this template will be sent to",
				sortable: false,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
			},
			{
				title: "Created",
				key: "createdAt",
				order: 4,
				description: "Date this template was created",
				sortable: false,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: false,
			},
			{
				title: "Updated",
				key: "lastUpdatedAt",
				order: 5,
				description: "Date this template was last updated",
				sortable: false,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: false,
			},
			{
				title: "Marked Deleted",
				key: "markDeletedAt",
				order: 6,
				description: "Date this template was deleted",
				sortable: false,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: false,
			},
		];

		return _columns;
	}

	/**
	 * Retrieves the modal items for the integration template setup.
	 * @returns {ModalItem[]}
	 */
	private get modalItems(): ModalItem[] {
		return [
			{
				title: "Title",
				key: "title",
				dataType: "text",
				required: true,
				maxLength: 50,
				description: "Enter a integration template title",
				placeholder: "Integration template title",
				readOnlyMethod: (item: ModalItem, row: any) => row?.groupSyncId != null,
			},
			{
				title: "Area",
				key: "groupId",
				dataType: "component",
				data: AreaTreeSelect,
				required: true,
				readOnlyMethod: (item: ModalItem, row: any) => row?.groupSyncId != null,
				props: {
					reduce: (area: AreaNode) => area.id,
					clearable: false,
				},
			},
			{
				title: "Device",
				key: "serverId",
				dataType: "vselect3",
				data: this.servers,
				required: true,
				readOnlyMethod: (item: ModalItem, row: any) => row?.groupSyncId != null,
				props: {
					reduce: (server: ServerDetails) => server.serverID,
				},
			},
			{
				title: "Default Contact Groups",
				key: "contactGroups",
				dataType: "vselect3",
				data: this.contactGroups,
				visible: this.isContactGroupsEnabled,
				readOnlyMethod: (item: ModalItem, row: any) => row?.groupSyncId != null,
				props: {
					onSearch: (searchTerm: string) => {
						this.handleSearchInput(searchTerm);
					},
					multiple: true,
				},
			},
			{
				title: "Default Users",
				key: "users",
				dataType: "vselect3",
				data: this.searchedContacts,
				visible: true,
				readOnlyMethod: (item: ModalItem, row: any) => row?.groupSyncId != null,
				props: {
					onSearch: (searchTerm: string) => {
						this.handleTemplateContactSearchInput(searchTerm);
					},
					multiple: true,
					closeOnSelect: false,
					getOptionLabel: (option) => option?.fullName + " (" + option?.email + ")",
				},
			},
			{
				title: "Subject",
				key: "subject",
				dataType: "text",
				required: true,
				maxLength: 50,
				description: "Enter a integration template subject",
				placeholder: "Integration template subject",
				readOnlyMethod: (item: ModalItem, row: any) => row?.groupSyncId != null,
			},
			{
				title: "Message",
				key: "message",
				dataType: "textarea",
				readOnlyMethod: (item: ModalItem, row: any) => row?.groupSyncId != null,
				placeholder: "Integration template message",
			},
			{
				title: "Parameters",
				key: "parameters",
				dataType: "textarea",
				required: false,
				maxLength: 1000,
				description: "Enter integration template parameters",
				placeholder: "Integration template parameters",
				readOnlyMethod: (item: ModalItem, row: any) => row?.groupSyncId != null,
			},
		];
	}

	/**
	 * Lifecycle hook called when the component is mounted.
	 * @returns {Promise<void>}
	 */
	private async mounted(): Promise<void> {
		try {
			this.servers = (await api.fetchNotificationServers()).data;
			await this.getPaginatedIntegrationTemplates();

			if (this.isContactGroupsEnabled) {
				await this.retrieveContactGroups();
			}
		} catch (error) {
			console.error("Error during component initialization:", error);
		}
	}

	/**
	 * Checks if the contact groups feature is enabled.
	 * @returns {boolean}
	 */
	private get isContactGroupsEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "NotificationGroups"]);
	}

	/**
	 * Checks if the integration templates feature is enabled.
	 * @returns {boolean}
	 */
	private get isIntegrationTemplatesEnabled(): boolean {
		return this.getFeature(["Actions", "MassNotification"]);
	}

	/**
	 * Checks if the user has permissions to view or edit integration templates.
	 * @returns {boolean}
	 */
	private get hasIntegrationTemplatePermissions(): boolean {
		return this.permissions.canViewIntegrationTemplates || this.permissions.canEditIntegrationTemplates;
	}

	/**
	 * Checks if the user has access to integration templates.
	 * @returns {boolean}
	 */
	private get hasIntegrationTemplateAccess(): boolean {
		return this.isIntegrationTemplatesEnabled && this.hasIntegrationTemplatePermissions;
	}

	/**
	 * Retrieves contact groups based on the search term.
	 * @param {string} searchTerm - The search term for contact groups.
	 * @returns {Promise<void>}
	 */
	private async retrieveContactGroups(searchTerm: string = ""): Promise<void> {
		const params: PaginatedSearchQueryParams = {
			page: 1,
			pageSize: 50,
			sortBy: "title",
			searchTerm: searchTerm,
		};

		this.contactGroupSearchToken = axios.CancelToken.source();

		try {
			const request: PaginatedSearchRequest = {
				params: params,
				cancelToken: this.contactGroupSearchToken.token,
			};
			this.contactGroups = (await api.getContactGroups(request)).data;
		} catch (err) {
			console.error("Error retrieving contact groups:", err);
			this.$notify({
				type: "error",
				title: "Retrieving Contact Groups",
				text: err.message,
			});
		}
	}

	/**
	 * Retrieves paginated integration templates.
	 * @param {PaginatedSearchQueryParams} [paginatedSearchQueryParams] - Optional search query parameters.
	 * @returns {Promise<void>}
	 */
	private async getPaginatedIntegrationTemplates(
		paginatedSearchQueryParams?: PaginatedSearchQueryParams
	): Promise<void> {
		this.isLoading = true;
		this.integrationTemplates = [];

		if (!paginatedSearchQueryParams) {
			paginatedSearchQueryParams = {
				page: 1,
				pageSize: 25,
				sortDesc: false,
				sortBy: "Title",
				searchTerm: "",
			};
		}

		const searchParams = this.generateNewPaginatedSearchRequest(paginatedSearchQueryParams);

		const request = { ...searchParams, ...paginatedSearchQueryParams } as IntegrationTemplatePagedRequest;

		this.mostRecentSearchParams = request;

		const results = await IntegrationTemplateApi.getIntegrationTemplates(request, request.groupId);
		this.totalRecords = results.totalRecords;
		this.integrationTemplates = results.data;
		this.isLoading = false;
	}

	/**
	 * Creates a new integration template.
	 * @param {IntegrationTemplate} integrationTemplate - The integration template to create.
	 * @returns {Promise<void>}
	 */
	private async createIntegrationTemplate(integrationTemplate: IntegrationTemplate): Promise<void> {
		await IntegrationTemplateApi.createIntegrationTemplate(integrationTemplate);
		await this.getPaginatedIntegrationTemplates(this.mostRecentSearchParams);
	}

	/**
	 * Updates an existing integration template.
	 * @param {IntegrationTemplate} integrationTemplate - The integration template to update.
	 * @returns {Promise<void>}
	 */
	private async updateIntegrationTemplate(integrationTemplate: IntegrationTemplate): Promise<void> {
		await IntegrationTemplateApi.updateIntegrationTemplate(integrationTemplate);
		await this.getPaginatedIntegrationTemplates(this.mostRecentSearchParams);
	}

	/**
	 * Deletes an integration template.
	 * @param {IntegrationTemplate} integrationTemplate - The integration template to delete.
	 * @returns {Promise<void>}
	 */
	private async deleteIntegrationTemplate(integrationTemplate: IntegrationTemplate): Promise<void> {
		await IntegrationTemplateApi.deleteIntegrationTemplate(integrationTemplate.integrationTemplateId);
		await this.getPaginatedIntegrationTemplates(this.mostRecentSearchParams);
	}

	/**
	 * Handles the search input for contact groups with debounce.
	 * @param {string} searchTerm - The search term for contact groups.
	 * @returns {Promise<void>}
	 */
	private handleSearchInput = debounce(
		async (searchTerm: string): Promise<void> => {
			await this.retrieveContactGroups(searchTerm);
		},
		getDebouncePeriod(),
		{ leading: false, trailing: true }
	);

	/**
	 * Handles the search input for template contacts.
	 * @param {string} searchTerm - The search term for contacts.
	 * @returns {Promise<void>}
	 */
	private async handleTemplateContactSearchInput(searchTerm: string): Promise<void> {
		if (!searchTerm || searchTerm.length < 1) {
			return;
		}
		this.searchedContacts = [];
		await this.templateContactSearch(searchTerm);
	}

	/**
	 * Searches for template contacts based on the query.
	 * @param {string} query - The search query.
	 * @returns {Promise<void>}
	 */
	private templateContactSearch: (query: string) => Promise<void> = debounce(
		async function (query: string): Promise<void> {
			if (this.templateContactSearchToken) {
				this.templateContactSearchToken.cancel("Cancel Search");
			}

			this.templateContactSearchToken = axios.CancelToken.source();
			this.isSearching = true;
			try {
				const { data } = await api.eventShareSearch(query);
				const contactsMapped: IntegrationTemplateContact[] = data.map((shareWithPerson: ShareWithPerson) => {
					return {
						userId: shareWithPerson.userID,
						fullName: shareWithPerson.name,
						email: shareWithPerson.email,
						contactType: IntegrationTemplateContactType.Contact,
					} as IntegrationTemplateContact;
				});

				contactsMapped.forEach((g) => {
					this.searchedContacts.push(g);
				});

				this.isSearching = false;
				return this.searchedContacts;
			} catch (e) {
				console.error("Error searching for template contacts:", e);
				this.$notify({
					type: "error",
					title: "Searching for Template Contacts",
					text: e.message,
				});
			}
			this.isSearching = false;
		},
		this.debouncePeriod,
		{ leading: false, trailing: true }
	);

	/**
	 * Handles the modal open event and sets the sync warning flag.
	 * @param {IntegrationTemplate} value - The integration template being edited.
	 * @returns {Promise<void>}
	 */
	private async onModalOpen(value: IntegrationTemplate): Promise<void> {
		this.showSyncWarning = value?.groupSyncId != null;
	}
}
